import { useEffect, useState } from 'react';
import { getWixBusinessHours } from '@rsmaids/shared/services/api/businessInfo';
import type { WixBusinessProfile } from '@rsmaids/shared/services/api/businessInfo';

type WeeklyHours = WixBusinessProfile['hours'];

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

// Used until the real hours have loaded, and on a day the business is closed.
// (Same default the Business Profile page uses for a newly opened day.)
const DEFAULT_OPEN_MINUTES = 9 * 60;
const DEFAULT_CLOSE_MINUTES = 17 * 60;

// One fetch for the whole app session; every calendar view shares it.
let weeklyHoursPromise: Promise<WeeklyHours> | null = null;
const loadWeeklyHours = (): Promise<WeeklyHours> => {
  if (!weeklyHoursPromise) {
    weeklyHoursPromise = getWixBusinessHours().catch(err => {
      console.error('[useBusinessHours]', err);
      weeklyHoursPromise = null; // allow a retry on the next mount
      return undefined;
    });
  }
  return weeklyHoursPromise;
};

const toMinutes = (hhmm: string | undefined): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm ?? '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

export interface BusinessHoursForDay {
  /** Opening time as minutes after midnight. */
  openMinutes: number;
  /** Closing time as minutes after midnight. */
  closeMinutes: number;
}

/** The business's opening hours for the weekday of `date`. */
export function useBusinessHours(date: Date): BusinessHoursForDay {
  const [weekly, setWeekly] = useState<WeeklyHours>(undefined);

  useEffect(() => {
    let cancelled = false;
    loadWeeklyHours().then(hours => {
      if (!cancelled) setWeekly(hours);
    });
    return () => { cancelled = true; };
  }, []);

  const day = weekly?.[DAY_KEYS[date.getDay()]];
  const open = toMinutes(day?.open);
  const close = toMinutes(day?.close);
  if (open === null || close === null || close <= open) {
    return { openMinutes: DEFAULT_OPEN_MINUTES, closeMinutes: DEFAULT_CLOSE_MINUTES };
  }
  return { openMinutes: open, closeMinutes: close };
}
